import { apiUrl } from "@/lib/aicheck/api-base";
import { authService } from "@/services/auth";

/**
 * 認証付き fetch の統一ヘルパー（client-safe）。
 *
 * Cognito の ID トークンを取得して `Authorization: Bearer <token>` を付与した
 * `fetch` を実行する。トークンが取得できない場合は、セッションの有無で文言を
 * 分けて例外を投げる（origin へ直接届く経路が無いことが前提の client 用）。
 *
 * 設計は SourceWorkspace.tsx で実績のあるローカル実装を踏襲している。
 */
export async function authedFetch(
  input: RequestInfo | URL,
  init: RequestInit,
): Promise<Response> {
  const token = await authService.getIdToken();
  if (!token) {
    const isAuthenticated = await authService.isAuthenticated();
    throw new Error(
      isAuthenticated
        ? "Cognito session is active, but the ID token could not be read. Reload the page and try again."
        : "Not authenticated. Please sign in again.",
    );
  }
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  // 別オリジン API 構成（ルート C′）では相対 /api パスをベース URL に解決する
  const target = typeof input === "string" ? apiUrl(input) : input;
  return fetch(target, { ...init, headers });
}

/**
 * `{ error?: string }` 形式の JSON API レスポンスを読み取る。
 * `response.ok` が false の場合は `error` フィールド（無ければ status）を
 * 文言にして例外を投げる。
 */
export async function readApiResponse<T>(response: Response): Promise<T> {
  const body = (await response.json().catch(() => null)) as {
    error?: unknown;
  } | null;
  if (!response.ok) {
    throw new Error(
      typeof body?.error === "string"
        ? body.error
        : `Request failed: ${response.status}`,
    );
  }
  return body as T;
}
